import { useState } from "react";
import { Delete, Lock } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Logo from "@/components/Logo";

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "back"];

export default function LockScreen() {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const { unlock } = useAuth();
  const nav = useNavigate();

  const press = (k) => {
    if (busy) return;
    if (k === "back") {
      setPin((p) => p.slice(0, -1));
      return;
    }
    if (pin.length >= 8) return;
    setPin((p) => p + k);
  };

  const submit = async () => {
    if (pin.length < 4) {
      toast.error("PIN must be at least 4 digits");
      return;
    }
    setBusy(true);
    try {
      await unlock(pin);
      toast.success("Unlocked");
      nav("/", { replace: true });
    } catch (e) {
      const msg = e.response?.data?.detail || "Incorrect PIN";
      toast.error(typeof msg === "string" ? msg : "Incorrect PIN");
      setPin("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center px-4" data-testid="lock-screen">
      <div className="w-full max-w-md animate-fade-up">
        <div className="text-center mb-10">
          <div className="flex justify-center mb-6">
            <Logo size="lg" />
          </div>
          <div className="inline-flex items-center gap-2 text-primary mb-2">
            <Lock strokeWidth={1.5} className="w-4 h-4" />
            <span className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Terminal locked</span>
          </div>
          <p className="text-sm text-muted-foreground mt-2 font-mono">Enter master PIN to unlock</p>
        </div>

        {/* PIN dots */}
        <div className="flex justify-center gap-3 mb-8" data-testid="pin-display">
          {Array.from({ length: Math.max(4, pin.length || 4) }).map((_, i) => (
            <div
              key={i}
              className={`w-3.5 h-3.5 rounded-full border border-border transition-colors ${
                i < pin.length ? "bg-primary border-primary" : "bg-transparent"
              }`}
            />
          ))}
        </div>

        {/* Keypad */}
        <div className="grid grid-cols-3 gap-3 w-full max-w-xs mx-auto">
          {KEYS.map((k, idx) => {
            if (k === "") return <div key={idx} />;
            if (k === "back") {
              return (
                <button
                  key={idx}
                  data-testid="pin-key-back"
                  onClick={() => press("back")}
                  className="aspect-square flex items-center justify-center border border-border bg-card hover:bg-muted active:scale-95 transition-all"
                >
                  <Delete strokeWidth={1.5} className="w-6 h-6" />
                </button>
              );
            }
            return (
              <button
                key={idx}
                data-testid={`pin-key-${k}`}
                onClick={() => press(k)}
                className="aspect-square flex items-center justify-center text-3xl font-heading font-medium border border-border bg-card hover:bg-muted active:scale-95 transition-all"
              >
                {k}
              </button>
            );
          })}
        </div>

        <button
          data-testid="pin-unlock-btn"
          onClick={submit}
          disabled={busy || pin.length < 4}
          className="mt-8 w-full max-w-xs mx-auto flex bg-primary text-primary-foreground py-4 font-heading font-semibold uppercase tracking-[0.15em] text-sm hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-40 items-center justify-center"
        >
          {busy ? "Unlocking..." : "Unlock"}
        </button>

        <p className="text-center text-xs text-muted-foreground mt-6 font-mono">
          Default PIN: 4321 (change in Admin → PIN)
        </p>
      </div>
    </div>
  );
}
